import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { PageHero, Eyebrow } from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import VideoTestimonials from "@/components/VideoTestimonials";
import { PaperPlane } from "@/components/PaperPlane";

const cases = [
  {
    type: "Home Services, Bradenton, FL",
    challenge: "Booked solid by referrals but invisible to anyone outside their network. Past ads ran with no clear message and no tracking.",
    did: "Rebuilt their positioning, scripted and coached the owner through authentic video, then launched tightly targeted local Meta campaigns.",
    result: "[X]x increase in qualified leads · [Y]% lower cost per lead [UPDATE]",
  },
  {
    type: "Wellness, Sarasota, FL",
    challenge: "Struggled to explain what made them different without sounding like every other studio in town.",
    did: "Clarified their ideal client and core message, then produced a series of voice-led video ads with local targeting.",
    result: "[X] new clients in 60 days · [Y]x ROAS [UPDATE]",
  },
  {
    type: "Professional Services, Tampa, FL",
    challenge: "High-trust offer, but a weak online presence meant prospects never heard their story.",
    did: "Built a full-service local market package, voice, video, and ongoing Meta ads management.",
    result: "Pipeline up [X]% · recognized name in their market [UPDATE]",
  },
];

const stats = [
  { v: "3.2x", l: "Avg. ROAS [UPDATE]" },
  { v: "12+", l: "Clients served [UPDATE]" },
  { v: "50+", l: "Video ads produced [UPDATE]" },
  { v: "4", l: "Counties served" },
];

export default function Results() {
  return (
    <>
      <Seo
        title="Results & Case Studies | Local Meta Ads Wins | Prolific Maven"
        description="See what happens when local Florida businesses finally own their voice. Case studies & testimonials from Bradenton, Sarasota & Tampa businesses."
        path="/results"
      />
      <PageHero
        eyebrow="Results"
        title="What happens when local businesses finally own their voice."
      />

      <section className="relative z-10 bg-ink-800 border-y border-white/10 py-24 sm:py-32" data-testid="case-studies">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 space-y-6">
          {cases.map((c, i) => (
            <Reveal key={i} delay={i * 90} className="bg-ink-900 border border-white/5 p-8 sm:p-12 grid lg:grid-cols-12 gap-8 hover:border-gold/40 transition-colors duration-500">
              <div className="lg:col-span-3">
                <PaperPlane className="w-7 h-7 mb-4" />
                <h2 className="font-display font-bold text-xl text-white">{c.type}</h2>
              </div>
              <div className="lg:col-span-3">
                <h3 className="text-xs uppercase tracking-widest text-gold mb-2">Challenge</h3>
                <p className="text-cream/70 text-sm leading-relaxed">{c.challenge}</p>
              </div>
              <div className="lg:col-span-3">
                <h3 className="text-xs uppercase tracking-widest text-gold mb-2">What we did</h3>
                <p className="text-cream/70 text-sm leading-relaxed">{c.did}</p>
              </div>
              <div className="lg:col-span-3">
                <h3 className="text-xs uppercase tracking-widest text-gold mb-2">Result</h3>
                <p className="text-white font-display font-semibold leading-snug">{c.result}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="relative z-10 bg-ink-900 py-24 sm:py-32" data-testid="results-testimonials">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <VideoTestimonials />
        </div>
      </section>

      <section className="relative z-10 bg-ink-800 border-y border-white/10" data-testid="results-stats">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
            {stats.map((s) => (
              <div key={s.l} className="bg-ink-800 p-10 sm:p-14 text-center">
                <div className="font-display font-black text-4xl sm:text-6xl text-gold">{s.v}</div>
                <div className="mt-3 text-cream/50 text-sm uppercase tracking-wider">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
